import { displayName, sampleBackup, type Person } from '@lineage/core';
import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Splash } from '../../components/Guard';
import { useToast } from '../../components/Toast';
import { api, ApiError } from '../../lib/api';
import { withCode } from '../../lib/errorCode';
import { useAuth } from '../../lib/auth';
import { keys, useOffline, useTree } from '../../lib/queries';
import { InviteDialog } from '../person/InviteDialog';
import { PersonForm, type FormTarget, type RelationType } from '../person/PersonForm';
import { ProfilePanel } from '../person/ProfilePanel';
import { PersonPicker } from '../perspective/Picker';
import { useTreeModel } from './model';
import { useView } from './store';
import { TreeCanvas } from './TreeCanvas';
import s from './TreePage.module.css';
import { useT } from '../../i18n';

const useIsMobile = () => {
  const [m, setM] = useState(() => window.matchMedia('(max-width: 800px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 800px)');
    const on = () => setM(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return m;
};

const dismissedKey = (id: string) => `lineage.whoPromptDismissed.${id}`;
const readDismissed = (id: string) => {
  try {
    return localStorage.getItem(dismissedKey(id)) === '1';
  } catch {
    return false;
  }
};

export function TreePage({ treeId }: { treeId: string }) {
  const t = useT();
  const tt = t.tree;
  const q = useTree(treeId);
  const snap = q.data;
  const qc = useQueryClient();
  const toast = useToast((st) => st.show);
  const { user } = useAuth();
  const mobile = useIsMobile();
  const selectedId = useView((v) => v.selectedId);
  const offline = useOffline((o) => o.offline);

  const [form, setForm] = useState<FormTarget | null>(null);
  const [invite, setInvite] = useState<{ person: Person | null } | null>(null);
  const [picker, setPicker] = useState<null | 'who' | 'see' | 'search'>(null);
  const [menu, setMenu] = useState(false);
  const [dismissed, setDismissed] = useState(() => readDismissed(treeId));
  const [cmd, setCmd] = useState(0);

  useEffect(() => {
    useView.getState().select(null);
  }, [treeId]);

  const meId = snap ? snap.me.perspectivePersonId ?? snap.me.personId : null;
  const empty = useMemo(() => ({ people: [], parentLinks: [], partnerships: [], version: 0 }), []);
  const model = useTreeModel(snap ?? empty, meId);
  const refresh = () => qc.invalidateQueries({ queryKey: keys.tree(treeId) });
  const fail = (e: unknown) => toast(e instanceof ApiError ? e.message : withCode(t.common.somethingWrong, e));

  const claim = useMutation({
    mutationFn: (personId: string) => api<{ status: string }>('POST', `/trees/${treeId}/claims`, { personId }),
    onSuccess: (r) => {
      toast(r.status === 'pending' ? tt.requestSent : tt.linked);
      void refresh();
    },
    onError: fail,
  });
  const seeAs = useMutation({
    mutationFn: (personId: string | null) => api('PUT', `/trees/${treeId}/perspective`, { personId }),
    onSuccess: refresh,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/trees/${treeId}/people/${id}`),
    onSuccess: () => {
      useView.getState().select(null);
      toast(tt.removed);
      void refresh();
    },
    onError: fail,
  });
  const sample = useMutation({
    mutationFn: () => api('POST', `/trees/${treeId}/import`, sampleBackup()),
    onSuccess: refresh,
    onError: fail,
  });
  const linkAll = useMutation({
    mutationFn: async ({ kids, parent, partner }: { kids: string[]; parent: string; partner: string }) => {
      for (const k of kids) await api('PUT', `/trees/${treeId}/people/${k}/parents`, { parentIds: [parent, partner] });
    },
    onSuccess: refresh,
    onError: fail,
  });

  if (q.isLoading) return <Splash />;
  if (q.error || !snap) {
    const notFound = (q.error as ApiError | null)?.status === 404;
    return (
      <div className="center">
        <div className="stack">
          <h1 style={{ fontSize: 30 }}>{notFound ? tt.notFound : tt.couldntLoad}</h1>
          <p className="muted">{notFound ? tt.notFoundHint : (q.error as Error | null)?.message}</p>
          <Link to="/trees" className="btn">{tt.backToTrees}</Link>
        </div>
      </div>
    );
  }

  const role = snap.me.role;
  const canEdit = role !== 'viewer';
  const isOwner = role === 'owner';
  const selected = selectedId ? model.graph.people.get(selectedId) : undefined;
  const linkedElsewhere = new Set<string>();
  const needsPrompt = snap.people.length > 0 && !meId && !dismissed;
  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(dismissedKey(treeId), '1');
    } catch {
      /* ignore */
    }
  };

  const pickWho = (personId: string) => {
    setPicker(null);
    claim.mutate(personId);
  };

  return (
    <div className={s.shell}>
      <header className={s.header}>
        <Link to="/trees" className="btn btn-ghost" aria-label={tt.allTrees}>←</Link>
        <h1 className={s.title}>{snap.tree.name}</h1>
        <button className="btn btn-ghost" type="button" onClick={() => setPicker('search')} aria-label={t.common.searchPeople}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="6.5" />
            <path d="M15.5 15.5 21 21" />
          </svg>
        </button>
        {canEdit && snap.people.length > 0 && (
          <button className="btn btn-ghost" type="button" onClick={() => setForm({ mode: 'create', anchorId: selected?.id ?? null, type: null, chooseWho: true })} aria-label={tt.addPerson}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span className={s.hideSm}>{tt.add}</span>
          </button>
        )}
        <button className={`btn btn-ghost ${s.hideSm}`} type="button" onClick={() => setCmd((c) => c + 1)}>{selected ? tt.centre : tt.fit}</button>
        <button className="btn btn-ghost" type="button" aria-label={tt.menu} aria-expanded={menu} onClick={() => setMenu((m) => !m)}>☰</button>
        {menu && (
          <nav className={s.menu} onClick={() => setMenu(false)}>
            <button type="button" onClick={() => setPicker('see')}>{tt.seeAs}</button>
            {snap.me.perspectivePersonId && <button type="button" onClick={() => seeAs.mutate(null)}>{tt.backToMe}</button>}
            <Link to="/t/$treeId/birthdays" params={{ treeId }}>{tt.birthdays}</Link>
            <Link to="/t/$treeId/list" params={{ treeId }}>{tt.asList}</Link>
            <Link to="/t/$treeId/members" params={{ treeId }}>{tt.membersSettings}</Link>
            {canEdit && <Link to="/t/$treeId/history" params={{ treeId }}>{tt.history}</Link>}
            <Link to="/settings">{tt.account}</Link>
          </nav>
        )}
      </header>
      {offline && <div role="status" className="offline-banner">{tt.offline}</div>}

      <div className={s.stage}>
        {snap.people.length > 0 && (
          <TreeCanvas key={treeId} model={model} meId={meId} focusY={mobile && selected ? 0.22 : 0.5} commandKey={cmd} />
        )}

        {snap.me.perspectivePersonId && (
          <div className={s.prompt} style={{ top: needsPrompt ? 100 : 12 }}>
            <div className="banner row">
              <span className="grow">{tt.seeingAs(displayName(model.graph.people.get(snap.me.perspectivePersonId) ?? ({ firstName: '…', lastName: null } as Person)))}</span>
              <button className="btn btn-sm" type="button" onClick={() => seeAs.mutate(null)}>{tt.backToMe}</button>
            </div>
          </div>
        )}

        {needsPrompt && (
          <div className={s.prompt}>
            <div className="card-panel stack">
              <strong>{tt.promptTitle}</strong>
              <span className="muted">{tt.promptBody}</span>
              <div className="row">
                <button className="btn btn-primary btn-sm" type="button" onClick={() => setPicker('who')}>{tt.findMyself}</button>
                <button className="btn btn-sm" type="button" onClick={() => setForm({ mode: 'create', anchorId: null, type: null, joinAsNew: true })}>{tt.notOnTree}</button>
                <button className="btn btn-sm" type="button" onClick={dismiss}>{tt.notNow}</button>
              </div>
            </div>
          </div>
        )}

        {snap.people.length === 0 && (
          <div className={s.empty}>
            <div className="card-panel stack" style={{ maxWidth: 380 }}>
              <h1 style={{ fontSize: 34 }}>{tt.startTitle}</h1>
              {canEdit ? (
                <>
                  <button className="btn btn-primary" type="button" onClick={() => setForm({ mode: 'create', anchorId: null, type: null, thisIsMe: true })}>{tt.addYourself}</button>
                  {isOwner && (
                    <button className="btn" type="button" disabled={sample.isPending} onClick={() => sample.mutate()}>{tt.trySample}</button>
                  )}
                </>
              ) : (
                <p className="muted">{tt.ownerHasntAdded}</p>
              )}
            </div>
          </div>
        )}

        {selected && (
          <ProfilePanel
            person={selected}
            graph={model.graph}
            labels={model.labels}
            pack={model.pack}
            kin={model.kins.get(selected.id)}
            isMe={selected.id === meId}
            isLinkedToAnAccount={false}
            onSelect={(id) => useView.getState().select(id)}
            onClose={() => useView.getState().select(null)}
            actions={{
              canEdit,
              canModify: isOwner || (canEdit && snap.me.ownPersonIds.includes(selected.id)),
              canInvite: canEdit,
              lastEdit: snap.lastEdits?.[selected.id],
              signedIn: Boolean(user),
              onEdit: () => setForm({ mode: 'edit', person: selected }),
              onAdd: (type: RelationType) => setForm({ mode: 'create', anchorId: selected.id, type }),
              onInvite: () => setInvite({ person: selected }),
              onThisIsMe: () => claim.mutate(selected.id),
              onSeeAs: () => seeAs.mutate(selected.id),
              onDelete: () => {
                if (window.confirm(tt.confirmRemove(displayName(selected)))) remove.mutate(selected.id);
              },
              onLinkAll: (kids, partner) => linkAll.mutate({ kids, parent: selected.id, partner }),
            }}
          />
        )}
      </div>

      {form && (
        <PersonForm
          treeId={treeId}
          graph={model.graph}
          target={form}
          canAddSelf={!meId}
          canChangeLinks={form.mode !== 'edit' || isOwner || snap.me.ownPersonIds.includes(form.person.id)}
          onClose={() => setForm(null)}
          onCreatedAsMe={async (id) => {
            await api('POST', `/trees/${treeId}/claims`, { personId: id });
          }}
          onSaved={(id) => {
            setForm(null);
            toast(t.common.saved);
            useView.getState().select(id);
          }}
        />
      )}
      {invite && <InviteDialog treeId={treeId} treeName={snap.tree.name} person={invite.person} isOwner={isOwner} onClose={() => setInvite(null)} />}
      {picker === 'who' && (
        <PersonPicker title={tt.whoAreYou} people={snap.people} disabledIds={linkedElsewhere} onPick={pickWho} onClose={() => setPicker(null)} onNone={() => { setPicker(null); setForm({ mode: 'create', anchorId: null, type: null, joinAsNew: true }); }} />
      )}
      {picker === 'see' && (
        <PersonPicker title={tt.seeAs} people={snap.people} onPick={(id) => { setPicker(null); seeAs.mutate(id); }} onClose={() => setPicker(null)} />
      )}
      {picker === 'search' && (
        <PersonPicker title={tt.findSomeone} people={snap.people} onPick={(id) => { setPicker(null); useView.getState().select(id); }} onClose={() => setPicker(null)} />
      )}
    </div>
  );
}
